/**
 * Script panel for displaying and playing back non-agent character dialogue lines during a SIM.
 *
 * @module components/sim/takeSim/OtherScript
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from 'react';
import {Typography, Grid} from '@material-ui/core';
import PropTypes from 'prop-types';
import {useStyles} from './OtherScript.js';
import AudioPlayButton from "../../common/AudioPlayButton";

/**
 * A React functional component that renders a non-agent character's
 * script details including character type, character name, and the script
 * text in a styled format. It also includes functionality for audio playback
 * if the script has an associated audio file or URL.
 *
 * @param {Object} props - The component's props.
 * @param {number} props.index - The index of the script entry, used to display order.
 * @param {Object} props.script - The script data object containing the following fields:
 *  - {Object} character: Details of the character performing the script.
 *      * {string} masked_type: The type of the character (masked or anonymized, if available).
 *      * {string} type: The type of the character.
 *      * {string} name: The name of the character.
 *  - {string} script: The script content or dialogue.
 *  - {string} id: The ID of the script for audio playback.
 *  - {boolean} audio: Whether an audio version of the script is available.
 *  - {string} audio_url: The URL of the audio version of the script (if available).
 *
 * @returns {React.Element} The rendered component showing script information and playback button.
 */
const OtherScript = ({
    index,
    script,
    noIndex,
}) => {
    const classes = useStyles();
    const [characterType, setCharacterType] = useState("");
    const [characterName, setCharacterName] = useState("");
    const [curScript, setCurScript] = useState({});

    useEffect(
        () => {
            setCurScript(script);
        },
        [script]
    );

    useEffect(
        () => {
            setCharacterType(curScript?.character?.masked_type?.length ?
                curScript?.character?.masked_type :
                curScript?.character?.type
            );
            setCharacterName(curScript?.character?.name ?? '');
        },
        [curScript]
    );

    return (
        <Grid container className={classes.customer}>
            {
                (curScript?.id && !noIndex) &&
                <Grid xs={1} item className={classes.index}>{index + 1}</Grid>
            }
            <Grid xs={11} item className={classes.scriptGrid}>
                <AudioPlayButton audio_id={script?.id}
                                 classes={classes}
                />
                <Typography className={`${classes.scriptTypo} ${classes.scriptStyle}`}>
                    <span className={`${classes.bold}`}>
                        {characterName}</span>,{' '}The{' '}{characterType}
                    <br/>
                    <span className={classes.script}>{script?.script}</span>
                </Typography>
                <div style={{clear: 'both'}}/>
            </Grid>
        </Grid>
    );
};

OtherScript.propTypes = {
    singleScript: PropTypes.object,
    index: PropTypes.number,
    currentAudioPlaying: PropTypes.any,
    audioPlaying: PropTypes.string,
    totalLength: PropTypes.number,
    callTranscript: PropTypes.number,
    setCallTranscript: PropTypes.func,
    currentAudio: PropTypes.object,
    setTimePlayfun: PropTypes.func,
    timePlay: PropTypes.number,
    pauseFunction: PropTypes.func,
    script: PropTypes.object,
    noIndex: PropTypes.bool,
};

export default OtherScript;
